import ExistingComponentSpecFields from 'components/common/existing-component-spec-fields';
import FormInput from 'components/common/form-input';
import EquipmentDetailsDisclosure from 'components/structure-page/equipment-details-disclosure';
import { existingSystemComponentTypeProperties as componentTypeProperties } from 'data/constants';
import type {
  ExistingSystemComponentType,
  PartialWithRequired
} from 'data/types';
import { updateExistingSystemComponent } from 'features/structure';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  componentHasSiblingsInSystem,
  isSelfContainedComponentType
} from 'utils/existing-equipment';

export default function ExistingSystemComponentForm({
  component,
  locked = false,
  specsCollapsible = false,
  specsExpanded = true,
  onToggleSpecs
}: {
  component: ExistingSystemComponentType;
  locked?: boolean;
  specsCollapsible?: boolean;
  specsExpanded?: boolean;
  onToggleSpecs?: () => void;
}) {
  const dispatch = useAppDispatch();
  const existingSystems = useAppSelector(
    (state) => state.structure.existingSystems
  );

  // Mixing prevention (AHA-6796): a component with siblings in its system
  // may not convert to a packaged (Self Contained) type — SC is a complete
  // standalone system. A standalone component keeps the full list. (An SC
  // component is always alone, so no reverse filter is needed.)
  const fieldsLocked = component.analyzing || locked;

  const componentHasSiblings = componentHasSiblingsInSystem(
    existingSystems,
    component.uniqueId
  );
  const componentTypeOptions = Object.keys(componentTypeProperties).filter(
    (type) =>
      !componentHasSiblings ||
      !isSelfContainedComponentType(type as ExistingSystemComponentType['type'])
  );

  const handlePropertyChange = (propKey: string, value: any) => {
    dispatch(
      updateExistingSystemComponent({
        uniqueId: component.uniqueId,
        [propKey]: value
      })
    );
  };

  const handleTypeChange = (newType: ExistingSystemComponentType['type']) => {
    const oldType = component.type;
    const oldProps =
      oldType && componentTypeProperties[oldType]
        ? componentTypeProperties[oldType]
        : {};
    const newPropsConfig =
      newType && componentTypeProperties[newType]
        ? componentTypeProperties[newType]
        : {};

    if (newType && !(newType in componentTypeProperties)) {
      console.error(`Invalid new component type selected: ${newType}`);
      return;
    }

    const propsToClear = Object.keys(oldProps).filter(
      (key) => !(key in newPropsConfig)
    );

    const updatePayload: PartialWithRequired<
      ExistingSystemComponentType,
      'uniqueId'
    > = {
      uniqueId: component.uniqueId,
      type: newType,
      typeSpecified: true
    };

    propsToClear.forEach((key) => {
      (updatePayload as any)[key] = undefined;
    });

    dispatch(updateExistingSystemComponent(updatePayload));
  };

  const currentPropertiesConfig =
    (component.type && componentTypeProperties[component.type]) || {};

  // Detail fields keep the AHA-6681 grouping (matching specs shown by
  // default, non-matching specs behind a collapsible section — shared with
  // the add-equipment modal via ExistingComponentSpecFields) while rendering
  // inside develop's specsCollapsible/EquipmentDetailsDisclosure layout
  // below. Manufacturer lives in the top row beside Model Number, so a
  // manufacturer-only type has no detail fields to disclose.
  const hasDetailFields =
    !!component.type &&
    Object.keys(currentPropertiesConfig).some((key) => key !== 'manufacturer');

  const detailFields = hasDetailFields ? (
    <ExistingComponentSpecFields
      componentType={component.type}
      values={component as Record<string, any>}
      onChange={handlePropertyChange}
      disabled={fieldsLocked}
    />
  ) : null;

  const typeAndModelInputs = (
    <>
      <FormInput
        type='select'
        label='Component Type'
        options={componentTypeOptions}
        value={component.type ?? ''}
        onChange={(value) =>
          handleTypeChange(value as ExistingSystemComponentType['type'])
        }
        placeholder='Select Type...'
        preventDeselection={false}
        disabled={fieldsLocked}
      />
      <FormInput
        type='text'
        label='Model Number'
        value={component.modelNumber ?? ''}
        onChange={(value) => handlePropertyChange('modelNumber', value)}
        placeholder='Enter Model #'
        disabled={fieldsLocked}
      />
      {'manufacturer' in currentPropertiesConfig && (
        <FormInput
          type='text'
          label='Manufacturer'
          value={(component as Record<string, any>).manufacturer ?? ''}
          onChange={(value) => handlePropertyChange('manufacturer', value)}
          placeholder='Enter Manufacturer'
          disabled={fieldsLocked}
        />
      )}
    </>
  );

  if (specsCollapsible) {
    return (
      <div className='flex flex-col gap-3'>
        <div className='flex flex-wrap items-center gap-x-5 gap-y-3'>
          {typeAndModelInputs}
        </div>
        {hasDetailFields && (
          <EquipmentDetailsDisclosure
            expanded={specsExpanded}
            onToggle={() => onToggleSpecs?.()}
          >
            <div className='flex flex-wrap items-center gap-x-5 gap-y-3 pb-0.5 pt-2'>
              {detailFields}
            </div>
          </EquipmentDetailsDisclosure>
        )}
      </div>
    );
  }

  return (
    <div className='flex flex-wrap items-center gap-x-5 gap-y-3 py-3'>
      {typeAndModelInputs}
      {detailFields}
    </div>
  );
}
